// Reaction mode per object type
export type ReactionMode = 'single' | 'multiple';

export interface ReactionMetadata {
  emoji: string;
  label: string;
  description: string;
  chart_color: string;
}

export interface ReactionConfig {
  mode: ReactionMode;
  types: string[];
  emojis: Record<string, string>;
}

// Reaction metadata for all reaction types
export const REACTION_METADATA: Record<string, ReactionMetadata> = {
  tasty: { emoji: '😋', label: 'Tasty', description: 'I enjoyed this', chart_color: '#8B5CF6' }, // Purple
  craving: { emoji: '🤤', label: 'Crave', description: 'I want this', chart_color: '#3B82F6' }, // Blue
  hot: { emoji: '🔥', label: 'Hot', description: 'Exciting', chart_color: '#F97316' }, // Orange
  love: { emoji: '❤️', label: 'Love', description: 'Favourite', chart_color: '#EF4444' }, // Red
  like: { emoji: '👍', label: 'Like', description: 'I like this', chart_color: '#10B981' }, // Green
  recommend: {
    emoji: '👍',
    label: 'Recommend',
    description: 'Approval / endorsement',
    chart_color: '#6366F1',
  }, // Indigo
  not_for_me: {
    emoji: '👎',
    label: 'Not for me',
    description: 'Clear negative signal',
    chart_color: '#6B7280',
  }, // Gray
  would_buy: { emoji: '🛒', label: 'Would buy', description: 'Would buy', chart_color: '#14B8A6' }, // Teal
  would_buy_again: {
    emoji: '🔁',
    label: 'Would buy again',
    description: 'Would buy again',
    chart_color: '#06B6D4',
  }, // Cyan
  gift_worthy: {
    emoji: '🎁',
    label: 'Gift-worthy',
    description: 'Gift-worthy',
    chart_color: '#EC4899',
  }, // Pink
  would_cook_with: {
    emoji: '🧑‍🍳',
    label: 'Would cook with',
    description: 'Would cook with',
    chart_color: '#F59E0B',
  }, // Amber
  would_try: { emoji: '🧪', label: 'Would try', description: 'Would try', chart_color: '#8B5CF6' }, // Violet
  celebrate: {
    emoji: '🎉',
    label: 'Celebrate',
    description: 'Celebrating this',
    chart_color: '#F59E0B',
  }, // Amber
  laugh: { emoji: '😂', label: 'Laugh', description: 'Funny', chart_color: '#FBBF24' }, // Yellow
};

export const REACTION_CONFIG: Record<string, ReactionConfig> = {
  item: {
    mode: 'multiple', // Users can add multiple different reactions
    types: [
      'tasty',
      'craving',
      'hot',
      'love',
      'recommend',
      'not_for_me',
      'would_buy',
      'would_buy_again',
      'gift_worthy',
      'would_cook_with',
      'would_try',
    ],
    emojis: {
      tasty: '😋',
      craving: '🤤',
      hot: '🔥',
      love: '❤️',
      recommend: '👍',
      not_for_me: '👎',
      would_buy: '🛒',
      would_buy_again: '🔁',
      gift_worthy: '🎁',
      would_cook_with: '🧑‍🍳',
      would_try: '🧪',
    },
  },
  collection: {
    mode: 'single', // Users can only have one reaction at a time
    types: ['love', 'recommend'],
    emojis: {
      love: '❤️',
      recommend: '👍',
    },
  },
  entity: {
    mode: 'multiple',
    types: ['love', 'recommend'],
    emojis: {
      love: '❤️',
      recommend: '👍',
    },
  },
  collection_item: {
    mode: 'multiple',
    types: ['tasty', 'craving', 'love', 'recommend'],
    emojis: {
      tasty: '😋',
      craving: '🤤',
      love: '❤️',
      recommend: '👍',
    },
  },
  submission: {
    mode: 'multiple',
    types: ['love', 'celebrate', 'laugh'],
    emojis: {
      love: '❤️',
      celebrate: '🎉',
      laugh: '😂',
    },
  },
  initiative: {
    mode: 'multiple',
    types: ['love', 'recommend', 'would_try'],
    emojis: {
      love: '❤️',
      would_try: '🧪',
      recommend: '👍',
    },
  },
  content: {
    mode: 'multiple',
    types: ['love', 'hot', 'recommend', 'would_try'],
    emojis: {
      love: '❤️',
      hot: '🔥',
      recommend: '👍',
      would_try: '🧪',
    },
  },
};

// Get reaction configuration for an object type
export function getReactionConfig(objectType: string): ReactionConfig {
  return (
    REACTION_CONFIG[objectType] || {
      mode: 'single',
      types: [],
      emojis: {},
    }
  );
}

// Get available reaction types for an object type
export function getAvailableReactions(objectType: string): string[] {
  return getReactionConfig(objectType).types;
}

// Get reaction mode for an object type
export function getReactionMode(objectType: string): ReactionMode {
  return getReactionConfig(objectType).mode;
}

// Get emoji for a reaction type
export function getReactionEmoji(objectType: string, reactionType: string): string {
  const config = getReactionConfig(objectType);
  return config.emojis[reactionType] || '👍';
}

// Check if object type allows multiple reactions
export function allowsMultipleReactions(objectType: string): boolean {
  return getReactionMode(objectType) === 'multiple';
}

// Get reaction metadata (emoji, label, description)
export function getReactionMetadata(reactionType: string): ReactionMetadata | undefined {
  return REACTION_METADATA[reactionType];
}

// Get all reaction metadata for a given object type
export function getReactionMetadataForObjectType(objectType: string): ReactionMetadata[] {
  const availableTypes = getAvailableReactions(objectType);
  return availableTypes
    .map((type) => REACTION_METADATA[type])
    .filter((metadata): metadata is ReactionMetadata => metadata !== undefined);
}
